import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  Plug, CheckCircle, XCircle, RefreshCw, Zap, Settings, Save
} from 'lucide-react';

const typeLabels = {
  email: 'Email Provider',
  sms: 'SMS Provider',
  push: 'Push Notifications',
  emr: 'EMR System',
  pharmacy: 'Pharmacy Integration',
  device: 'Device Integration',
};

const IntegrationManagement = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const [testing, setTesting] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(null); // "type:provider"
  const [settingsDraft, setSettingsDraft] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const res = await api.get('/integrations/config');
      if (res.data?.success) {
        const data = res.data.data;
        // Backend returns grouped { emr: [], pharmacy: [], device: [] }
        const flat = Array.isArray(data) ? data : Object.values(data || {}).flat();
        setConfigs(flat);
      }
    } catch {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfigs(); }, []);

  const handleSwitch = async (type, provider) => {
    setSwitching(`${type}:${provider}`);
    try {
      await api.put(`/integrations/config/${type}/switch`, { provider });
      toast.success(`Switched ${typeLabels[type] || type} provider`);
      await loadConfigs();
    } catch {
      toast.error('Failed to switch provider');
    } finally {
      setSwitching(null);
    }
  };

  const handleTest = async (type, provider) => {
    setTesting(`${type}:${provider}`);
    try {
      const res = await api.post(`/integrations/config/${type}/${provider}/test`);
      if (res.data?.success) {
        toast.success(res.data.message || 'Connection successful');
        await loadConfigs();
      } else {
        toast.error(res.data.error || res.data.message || 'Test failed');
      }
    } catch {
      toast.error('Connection test failed');
    } finally {
      setTesting(null);
    }
  };

  const openSettings = (cfg) => {
    const key = `${cfg.type}:${cfg.provider}`;
    setSettingsDraft({ ...(cfg.settings || {}) });
    setSettingsOpen(key);
  };

  const saveSettings = async (type, provider) => {
    setSavingSettings(true);
    try {
      await api.put(`/integrations/config/${type}/${provider}/settings`, { settings: settingsDraft });
      toast.success('Settings saved');
      setSettingsOpen(null);
      await loadConfigs();
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Providers that expose configurable settings fields
  const SETTINGS_FIELDS = {
    'emr:hapi_fhir': [
      { key: 'baseUrl', label: 'FHIR Server URL', placeholder: 'https://hapi.fhir.org/baseR4', type: 'text' }
    ]
  };

  if (loading) {
    return (
      <div className="space-y-4 py-6">
        {[1, 2, 3].map(i => <div key={i} className="h-32 rounded-2xl animate-pulse bg-slate-100" />)}
      </div>
    );
  }

  // Group configs by type
  const grouped = configs.reduce((acc, cfg) => {
    const type = cfg.type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(cfg);
    return acc;
  }, {});

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">Integrations</h2>
          <p className="text-slate-500 text-sm mt-1">Manage external service providers and connections</p>
        </div>
        <button onClick={loadConfigs} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-outline-variant/10">
          <Plug className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <h3 className="font-bold text-lg text-slate-900 mb-1">No Integrations Configured</h3>
          <p className="text-sm text-slate-500">Run the seed script to initialize default integration configs.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([type, providers]) => (
          <div key={type} className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" /> {typeLabels[type] || type}
              </h3>
              <p className="text-xs text-slate-500 mt-1">{providers.length} provider{providers.length !== 1 ? 's' : ''} available</p>
            </div>
            <div className="divide-y divide-slate-100">
              {providers.map((cfg) => {
                const key = `${type}:${cfg.provider}`;
                const isActive = cfg.isActive;
                const hasSettings = !!SETTINGS_FIELDS[key];
                const isSettingsOpen = settingsOpen === key;
                return (
                  <div key={cfg._id || key}>
                    <div className="flex items-center gap-4 p-5">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                        {isActive ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-slate-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-900">{cfg.displayName || cfg.provider}</p>
                        <p className="text-xs text-slate-500 truncate">{cfg.description || ''}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {isActive ? 'Active' : 'Inactive'}
                          {cfg.tier && ` · ${cfg.tier} tier`}
                          {cfg.lastTestedAt && ` · Tested ${new Date(cfg.lastTestedAt).toLocaleDateString()}`}
                          {cfg.status === 'error' && cfg.lastError && ` · Error: ${cfg.lastError}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {hasSettings && (
                          <button
                            onClick={() => isSettingsOpen ? setSettingsOpen(null) : openSettings(cfg)}
                            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${isSettingsOpen ? 'bg-slate-200 text-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                          >
                            <Settings className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleTest(type, cfg.provider)}
                          disabled={testing === key}
                          className="px-3 py-1.5 text-xs font-bold bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-all disabled:opacity-50"
                        >
                          {testing === key ? 'Testing...' : 'Test'}
                        </button>
                        {!isActive && (
                          <button
                            onClick={() => handleSwitch(type, cfg.provider)}
                            disabled={switching === key}
                            className="px-3 py-1.5 text-xs font-bold bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-all disabled:opacity-50"
                          >
                            {switching === key ? 'Switching...' : 'Activate'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Settings panel for configurable providers */}
                    {isSettingsOpen && hasSettings && (
                      <div className="px-5 pb-5 bg-slate-50 border-t border-slate-100">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest pt-4 mb-3">Settings</p>
                        <div className="space-y-3">
                          {SETTINGS_FIELDS[key].map(field => (
                            <div key={field.key}>
                              <label className="block text-xs font-bold text-slate-700 mb-1">{field.label}</label>
                              <input
                                type={field.type || 'text'}
                                placeholder={field.placeholder}
                                value={settingsDraft[field.key] || ''}
                                onChange={e => setSettingsDraft(d => ({ ...d, [field.key]: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
                              />
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={() => saveSettings(type, cfg.provider)}
                            disabled={savingSettings}
                            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 transition-all disabled:opacity-50"
                          >
                            <Save className="w-3.5 h-3.5" />
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                          </button>
                          <button
                            onClick={() => setSettingsOpen(null)}
                            className="px-4 py-2 bg-slate-200 text-slate-700 text-xs font-bold rounded-lg hover:bg-slate-300 transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default IntegrationManagement;
